/**
 * Yearly costs maths - w2_16.
 *
 * A yearly cost: { id, name, emoji, amount, next_date: 'YYYY-MM-DD',
 *   every: 'once' | 'year' | 'half' | 'quarter' | 'custom', months (custom),
 *   fund_id (optional sinking fund it's saved in) }
 *
 * Each repeating cost is spread evenly: a $620 cost twice a year is $103.33 a
 * month. A one-off cost is spread over the months until it's due.
 */

export const FREQS = [
  { k: 'year', label: 'Every year', months: 12 },
  { k: 'half', label: 'Twice a year', months: 6 },
  { k: 'quarter', label: 'Every 3 months', months: 3 },
  { k: 'once', label: 'Just once', months: 0 },
  { k: 'custom', label: 'Custom', months: null },
];

const n = (v) => { const x = Number(v); return Number.isFinite(x) ? x : 0; };
const day = (k) => new Date(String(k).slice(0, 10) + 'T12:00:00');
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12);

export const intervalOf = (c) => {
  if (!c || c.every === 'once') return 0;
  if (c.every === 'custom') return Math.max(1, Math.round(n(c.months) || 12));
  return { year: 12, half: 6, quarter: 3 }[c.every] || 12;
};

// The same day of the month, n months later (Jan 31 + 1 month = Feb 28).
export const addMonths = (d, m) => {
  const y = d.getFullYear();
  const mo = d.getMonth() + m;
  const target = new Date(y, mo, 1, 12);
  const last = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(d.getDate(), last));
  return target;
};

// Whole months from a to b, counting the month of b (Nov 10 -> Dec 18 is 2).
const monthsUntil = (from, to) => Math.max(1, (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth()) + 1);

/** The next time it's due, today or later. null for a one-off that's passed. */
export const nextDue = (c, today = new Date()) => {
  if (!c || !c.next_date) return null;
  const t = startOfDay(today);
  const start = day(c.next_date);
  const iv = intervalOf(c);
  if (!iv) return start >= t ? start : null;
  if (start >= t) return start;
  let k = 1;
  let d = addMonths(start, iv);
  while (d < t && k < 2400) { k += 1; d = addMonths(start, iv * k); }
  return d;
};

/** What to set aside each month for it. */
export const perMonth = (c, today = new Date()) => {
  const amt = n(c && c.amount);
  if (amt <= 0) return 0;
  const iv = intervalOf(c);
  if (iv) return amt / iv;
  const due = nextDue(c, today);
  if (!due) return 0;
  return amt / monthsUntil(startOfDay(today), due);
};

/** How much it costs over a year. A one-off counts if it's due in the next 12 months. */
export const perYear = (c, today = new Date()) => {
  const amt = n(c && c.amount);
  if (amt <= 0) return 0;
  const iv = intervalOf(c);
  if (iv) return (amt * 12) / iv;
  const due = nextDue(c, today);
  return due && due < addMonths(startOfDay(today), 12) ? amt : 0;
};

/** Every date it falls on in a calendar year. */
export const datesInYear = (c, year) => {
  if (!c || !c.next_date) return [];
  const start = day(c.next_date);
  const iv = intervalOf(c);
  if (!iv) return start.getFullYear() === year ? [start] : [];
  const diff = (year - start.getFullYear()) * 12 - start.getMonth();
  const k0 = Math.floor(diff / iv) - 1;
  const out = [];
  for (let k = k0; k <= k0 + Math.ceil(12 / iv) + 2; k += 1) {
    const d = addMonths(start, iv * k);
    if (d.getFullYear() === year) out.push(d);
  }
  return out;
};

/** How much is due in each month of a calendar year, and which costs. */
export const monthTotals = (costs, year) => {
  const totals = Array.from({ length: 12 }, () => ({ total: 0, names: [] }));
  (costs || []).forEach((c) => {
    datesInYear(c, year).forEach((d) => {
      const m = totals[d.getMonth()];
      m.total += n(c.amount);
      if (!m.names.includes(c.name)) m.names.push(c.name);
    });
  });
  return totals;
};

/** The month that stands out, if one does: at least 1.6 times the average month that has something due. */
export const bigMonth = (totals) => {
  const withCosts = totals.filter((m) => m.total > 0);
  if (withCosts.length < 2) return null;
  let best = 0;
  totals.forEach((m, i) => { if (m.total > totals[best].total) best = i; });
  const avg = withCosts.reduce((s, m) => s + m.total, 0) / withCosts.length;
  return totals[best].total >= avg * 1.6 ? { month: best, ...totals[best] } : null;
};

/** Is the linked fund ready? { has, toGo, ready } or null if there's no fund. */
export const readiness = (c, funds) => {
  if (!c || !c.fund_id) return null;
  const f = (funds || []).find((x) => String(x.id) === String(c.fund_id));
  if (!f) return null;
  const has = n(f.balance);
  const toGo = Math.max(0, n(c.amount) - has);
  return { fund: f, has, toGo, ready: toGo <= 0.004 };
};

export const freqLabel = (c) => {
  const iv = intervalOf(c);
  if (!iv) return 'once';
  if (iv === 12) return 'every year';
  if (iv === 6) return 'twice a year';
  if (iv === 3) return 'every 3 months';
  if (iv === 1) return 'every month';
  if (iv % 12 === 0) return `every ${iv / 12} years`;
  return `every ${iv} months`;
};

/** The next 12 months, starting this month: [{ date, total, names }]. */
export const nextTwelve = (costs, today = new Date()) => {
  const y = today.getFullYear();
  const byYear = { [y]: monthTotals(costs, y), [y + 1]: monthTotals(costs, y + 1) };
  return Array.from({ length: 12 }, (_, i) => {
    const d = new Date(y, today.getMonth() + i, 1, 12);
    const m = byYear[d.getFullYear()][d.getMonth()];
    return { date: d, total: m.total, names: m.names };
  });
};
